import { useCallback } from "react"
import { useFragment } from "react-relay"
import { formatDate } from "@/lib/format-date"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { chReferralLetterFragment$key } from "@/modules/patients/detail/referral/ch/services/__generated__/chReferralLetterFragment.graphql"
import { chReferralLetterFragment } from "@/modules/patients/detail/referral/ch/services/chReferralLetterFragment"
import { useChGenerateReferralDocumentMutation } from "@/modules/patients/detail/referral/ch/services/useChGenerateReferralDocumentMutation"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientAllergiesOutputFragment$key } from "@/modules/patients/services/__generated__/PatientAllergiesOutputFragment.graphql"
import { PatientConditionInformationOutputFragment$key } from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { PatientAllergiesOutputFragment } from "@/modules/patients/services/PatientAllergiesOutputFragment"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"

type Args = {
  progressNote: ProgressNoteData
}

const useRegenerateReferralDocument = ({ progressNote }: Args) => {
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const appointmentId = useAppointmentParam()

  const [generateReferralDocument] = useChGenerateReferralDocumentMutation()

  const [patientData] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const conditionInformationRef = patientData.patientById?.conditionInformation ?? null
  const allergiesInformationRef = patientData.patientById?.allergiesInformation ?? null

  const { activeMedications, inactiveMedications } = useMedications()
  const conditionFragment = useFragment<PatientConditionInformationOutputFragment$key>(
    PatientConditionInformationOutputFragment,
    conditionInformationRef,
  )
  const allergyFragment = useFragment<PatientAllergiesOutputFragment$key>(
    PatientAllergiesOutputFragment,
    allergiesInformationRef,
  )

  const referralLetterRef =
    progressNote.__typename === "ch_ProgressNote"
      ? (progressNote.referralLetter as chReferralLetterFragment$key | null)
      : null
  const referralLetterData = useFragment(chReferralLetterFragment, referralLetterRef)

  const regenerateReferralDocument = useCallback(
    (keepOriginalAuthor = false) =>
      new Promise<void>((resolve) => {
        if (!referralLetterData) {
          resolve()
          return
        }

        const conditionList = conditionFragment?.conditions ?? []
        const allergyList = allergyFragment?.allergies ?? []
        const medicationList = [...activeMedications, ...inactiveMedications]

        const allergies = (referralLetterData.allergies ?? []).map(
          (id) => allergyList.find((item) => item.icdDiagnosis.id === id)?.icdDiagnosis.text ?? "",
        )
        const conditions = (referralLetterData.conditions ?? []).map(
          (id) => conditionList.find((item) => item.icdDiagnosis.id === id)?.icdDiagnosis.text ?? "",
        )
        const medications = (referralLetterData.medicationIds ?? []).map(
          (id) => medicationList.find((item) => item.id === id)?.name ?? "",
        )

        generateReferralDocument({
          params: {
            id: referralLetterData.id,
            allergies: allergies.join(", ") || "-",
            conditions: conditions.join(", ") || "-",
            medications: medications.join(", ") || "-",
            reasoning: referralLetterData.reasoning ?? "",
            episodeOfCare: referralLetterData.icdDiagnosis.text,
            dateOfConsultation: formatDate(new Date(progressNote.createdAt)),
            keepOriginalAuthor,
          },
          onCompleted: () => resolve(),
          onError: () => resolve(),
        })
      }),
    [
      referralLetterData,
      conditionFragment,
      allergyFragment,
      activeMedications,
      inactiveMedications,
      generateReferralDocument,
      progressNote.createdAt,
    ],
  )

  return { regenerateReferralDocument }
}

export { useRegenerateReferralDocument }
